import React, { useCallback, useMemo, useState } from 'react';
import { Filter, Plus } from 'lucide-react';
import {
  Button,
  PHONE_OR_TOUCH_QUERY,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useMediaQuery,
} from '@bastion/ui';
import type { ActiveFilter, FilterConfig, FilterState } from '../types';
import { FilterChip } from '../FilterChip/FilterChip';
import { FilterMenu } from '../FilterMenu/FilterMenu';
import css from './ListViewToolbar.module.css';

export interface ListViewToolbarProps {
  filterConfigs: FilterConfig[];
  value: FilterState;
  onChange: (next: FilterState) => void;
  /** Optional leading slot rendered to the left of the chips (e.g. preset tabs). */
  leading?: React.ReactNode;
  /** Optional footer rendered at the bottom of every filter-menu popover. */
  filterMenuFooter?: React.ReactNode;
  /** Optional trailing slot rendered after the filter icon button (e.g. display options). */
  trailingActions?: React.ReactNode;
  /** Move the page actions while the presets and chips stay in the list. */
  renderActions?: (actions: React.ReactNode) => React.ReactNode;
}

/**
 * On a phone or a touch screen the filter menu box takes focus when it
 * opens, not its search field. Focus in the field opens the touch keyboard
 * over the menu. With focus in the box, Tab and a screen reader go into the
 * menu. Radix calls this on the box (tabIndex -1).
 */
function focusMenuBox(event: Event) {
  event.preventDefault();
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.focus({ preventScroll: true });
  }
}

function findConfig(configs: FilterConfig[], id: string): FilterConfig | undefined {
  return configs.find((x) => x.id === id);
}

function isChipActive(filter: ActiveFilter, config: FilterConfig): boolean {
  if (filter.values.length === 0) {
    return false;
  }
  if (config.kind === 'text-search') {
    return Boolean(filter.values[0]?.trim().length);
  }
  if (config.kind === 'date-range') {
    const from = filter.values[0]?.trim() ?? '';
    const to = filter.values[1]?.trim() ?? '';
    return from.length > 0 || to.length > 0;
  }
  if (config.kind === 'numeric-range') {
    const minV = filter.values[0]?.trim() ?? '';
    const maxV = filter.values[1]?.trim() ?? '';
    return minV.length > 0 || maxV.length > 0;
  }
  return filter.values.length > 0;
}

export function ListViewToolbar({
  filterConfigs,
  value,
  onChange,
  leading,
  filterMenuFooter,
  trailingActions,
  renderActions,
}: ListViewToolbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [inlineMenuOpen, setInlineMenuOpen] = useState(false);
  const phoneOrTouch = useMediaQuery(PHONE_OR_TOUCH_QUERY);

  const updateFilter = useCallback(
    (attributeId: string, next: ActiveFilter) => {
      onChange({
        filters: value.filters.map((f) =>
          f.attributeId === attributeId ? next : f,
        ),
      });
    },
    [onChange, value.filters],
  );

  const removeFilter = useCallback(
    (attributeId: string) => {
      const config = findConfig(filterConfigs, attributeId);
      const removeIds = new Set([attributeId, ...(config?.kind === 'multi-select' ? config.removeWith ?? [] : [])]);
      onChange({
        filters: value.filters.filter((f) => !removeIds.has(f.attributeId)),
      });
    },
    [filterConfigs, onChange, value.filters],
  );

  const hasAnyConfig = filterConfigs.length > 0;
  const activeChips = useMemo(
    () =>
      value.filters
        .map((f) => ({ filter: f, config: findConfig(filterConfigs, f.attributeId) }))
        .filter((entry): entry is { filter: ActiveFilter; config: FilterConfig } => {
          if (!entry.config) {
            return false;
          }
          return isChipActive(entry.filter, entry.config);
        }),
    [filterConfigs, value.filters],
  );

  const actions = (
    <div className={css.actions}>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={css.filterTrigger}
            aria-label="Filter"
            data-testid="filter-add-button"
            disabled={!hasAnyConfig}
          >
            <Filter style={{ width: 14, height: 14 }} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={6}
          className={css.menuPopover}
          onOpenAutoFocus={phoneOrTouch ? focusMenuBox : undefined}
        >
          <FilterMenu
            filterConfigs={filterConfigs}
            value={value}
            onChange={onChange}
            onClose={() => setMenuOpen(false)}
          />
          {filterMenuFooter ? (
            <div className={css.menuFooter}>{filterMenuFooter}</div>
          ) : null}
        </PopoverContent>
      </Popover>
      {trailingActions}
    </div>
  );

  return (
    <div className={css.toolbar} data-testid="list-view-toolbar">
      {leading ? <div className={css.leading}>{leading}</div> : null}
      <div className={css.chips}>
        {activeChips.map(({ filter, config }) => (
          <FilterChip
            key={filter.attributeId}
            config={config}
            filter={filter}
            filterState={value}
            filterConfigs={filterConfigs}
            onFilterStateChange={onChange}
            onFilterChange={(next) => updateFilter(filter.attributeId, next)}
            onRemove={() => removeFilter(filter.attributeId)}
          />
        ))}
        {activeChips.length > 0 ? (
          <Popover open={inlineMenuOpen} onOpenChange={setInlineMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={css.inlineAddButton}
                aria-label="Add filter"
                data-testid="filter-add-inline"
                disabled={!hasAnyConfig}
              >
                <Plus style={{ width: 14, height: 14 }} />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={6}
              className={css.menuPopover}
              onOpenAutoFocus={phoneOrTouch ? focusMenuBox : undefined}
            >
              <FilterMenu
                filterConfigs={filterConfigs}
                value={value}
                onChange={onChange}
                onClose={() => setInlineMenuOpen(false)}
              />
              {filterMenuFooter ? (
                <div className={css.menuFooter}>{filterMenuFooter}</div>
              ) : null}
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
      {renderActions ? renderActions(actions) : actions}
    </div>
  );
}
